import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface CookieConsents {
  essential: boolean;
  analytics: boolean;
  marketing: boolean;
  functional: boolean;
}

interface StoredConsent {
  consents: CookieConsents;
  timestamp: number;
  expiresAt: number;
}

const CONSENT_STORAGE_KEY = 'cookie_consents';
const CONSENT_SESSION_KEY = 'cookie_consents_session';
const CONSENT_DURATION_MS = 13 * 30 * 24 * 60 * 60 * 1000; // 13 months in milliseconds

export const useCookieConsent = () => {
  const [consents, setConsents] = useState<CookieConsents | null>(null);
  const [hasAnswered, setHasAnswered] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Load consents from localStorage or sessionStorage
    const loadConsents = () => {
      // Check if storage is available (Safari private mode / tracking prevention)
      const isStorageAvailable = () => {
        try {
          const test = '__storage_test__';
          localStorage.setItem(test, test);
          localStorage.removeItem(test);
          return true;
        } catch (e) {
          return false;
        }
      };

      if (!isStorageAvailable()) {
        console.warn('⚠️ localStorage not available (Safari tracking prevention or private mode)');
        setIsLoading(false);
        return;
      }

      // Try localStorage first
      try {
        const stored = localStorage.getItem(CONSENT_STORAGE_KEY);
        if (stored) {
          const parsed: StoredConsent = JSON.parse(stored);
          const now = Date.now();
          
          // Check if consent has expired
          if (parsed.expiresAt && parsed.expiresAt > now) {
            console.log('✅ Cookie consents loaded from localStorage (expires:', new Date(parsed.expiresAt).toLocaleDateString(), ')');
            setConsents(parsed.consents);
            setHasAnswered(true);
            
            // Backup to sessionStorage
            try {
              sessionStorage.setItem(CONSENT_SESSION_KEY, stored);
            } catch (e) {
              console.warn('⚠️ sessionStorage not available:', e);
            }
            return;
          } else {
            console.log('⏰ Cookie consents expired, clearing...');
            localStorage.removeItem(CONSENT_STORAGE_KEY);
            try {
              sessionStorage.removeItem(CONSENT_SESSION_KEY);
            } catch (e) {
              console.warn('⚠️ sessionStorage not available:', e);
            }
          }
        }
      } catch (e) {
        console.error('❌ Failed to parse cookie consents from localStorage:', e);
      }

      // Try sessionStorage as backup
      try {
        const sessionStored = sessionStorage.getItem(CONSENT_SESSION_KEY);
        if (sessionStored) {
          const parsed: StoredConsent = JSON.parse(sessionStored);
          const now = Date.now();
          
          if (parsed.expiresAt && parsed.expiresAt > now) {
            console.log('✅ Cookie consents restored from sessionStorage backup');
            setConsents(parsed.consents);
            setHasAnswered(true);
            
            // Restore to localStorage
            try {
              localStorage.setItem(CONSENT_STORAGE_KEY, sessionStored);
            } catch (e) {
              console.warn('⚠️ localStorage not available:', e);
            }
            return;
          }
        }
      } catch (e) {
        console.error('❌ Failed to parse cookie consents from sessionStorage:', e);
      }

      console.log('ℹ️ No valid cookie consents found, showing banner');
    };

    loadConsents();
    setIsLoading(false);
  }, []);

  const saveConsents = async (newConsents: CookieConsents) => {
    const now = Date.now();
    const expiresAt = now + CONSENT_DURATION_MS;
    
    const storedConsent: StoredConsent = {
      consents: newConsents,
      timestamp: now,
      expiresAt
    };

    const serialized = JSON.stringify(storedConsent);
    
    // Save to both localStorage and sessionStorage (with error handling for Safari)
    try {
      localStorage.setItem(CONSENT_STORAGE_KEY, serialized);
    } catch (e) {
      console.warn('⚠️ Could not save to localStorage (Safari tracking prevention?):', e);
    }
    
    try {
      sessionStorage.setItem(CONSENT_SESSION_KEY, serialized);
    } catch (e) {
      console.warn('⚠️ Could not save to sessionStorage:', e);
    }
    
    console.log('💾 Cookie consents saved (expires:', new Date(expiresAt).toLocaleDateString(), ')');
    
    setConsents(newConsents);
    setHasAnswered(true);

    // Try to save to database if user is authenticated
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (user) {
        await supabase.functions.invoke('record-user-consents', {
          body: {
            user_id: user.id,
            consents: {
              essential: newConsents.essential,
              analytics: newConsents.analytics,
              marketing: newConsents.marketing,
              functional: newConsents.functional
            }
          }
        });
        console.log('📤 Cookie consents synced to database');
      }
    } catch (error) {
      console.error('❌ Failed to save consents to database:', error);
    }
  };

  const acceptAll = () => {
    saveConsents({
      essential: true,
      analytics: true,
      marketing: true,
      functional: true
    });
  };

  const rejectAll = () => {
    saveConsents({
      essential: true, // Essential cookies cannot be refused
      analytics: false,
      marketing: false,
      functional: false
    });
  };

  return {
    consents,
    hasAnswered,
    isLoading,
    saveConsents,
    acceptAll,
    rejectAll
  };
};
